import React, { useState, useEffect } from 'react';
import { X, User, Check } from 'lucide-react';
import { supabase } from '../lib/db.ts';
import { fetchMarketCategories, Category } from '../services/categoryService.ts';

interface AddPartnerModalProps {
  onClose: () => void;
  onAdd: () => void;
}

export function AddPartnerModal({ onClose, onAdd }: AddPartnerModalProps) {
  const [users, setUsers] = useState<any[]>([]);
  const [selectedUser, setSelectedUser] = useState<any | null>(null);
  const [partnerName, setPartnerName] = useState('');
  const [commissionRate, setCommissionRate] = useState(1);
  const [allowedCategories, setAllowedCategories] = useState<string[]>([]);
  const [maxMarketsPerDay, setMaxMarketsPerDay] = useState(50);
  const [categories, setCategories] = useState<Category[]>([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filteredUsers, setFilteredUsers] = useState<any[]>([]);

  // Fetch categories on component mount
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const categoriesData = await fetchMarketCategories();
        setCategories(categoriesData);
      } catch (err) {
        console.error('Failed to load categories:', err);
      }
    };
    loadCategories();
  }, []);

  useEffect(() => {
    // Fetch all users who are not already partners
    const fetchUsers = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data, error } = await supabase.rpc('admin_list_users');
        if (error) throw error;
        // Filter out users who are already partners
        const nonPartners = (data || []).filter((u: any) => u.role !== 'partner');
        setUsers(nonPartners);
        setFilteredUsers(nonPartners);
      } catch (err) {
        setError('Failed to load users');
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, []);

  useEffect(() => {
    if (!search.trim()) {
      setFilteredUsers(users);
    } else {
      setFilteredUsers(users.filter(u =>
        u.email.toLowerCase().includes(search.toLowerCase())
      ));
    }
  }, [search, users]);

  const handleCategoryToggle = (cat: string) => {
    setAllowedCategories(prev =>
      prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) {
      setError('Please select a user');
      return;
    }
    if (!partnerName.trim()) {
      setError('Partner name is required');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // 1. Update user role to partner
      const { error: roleError } = await supabase.rpc('admin_update_user_role', {
        target_user_id: selectedUser.id,
        new_role: 'partner'
      });
      if (roleError) throw roleError;
      // 2. Update partner metadata using the new RPC
      const { error: metaError } = await supabase.rpc('admin_update_partner_metadata', {
        p_user_id: selectedUser.id,
        p_partner_name: partnerName,
        p_commission_rate: commissionRate / 100,
        p_allowed_categories: allowedCategories,
        p_max_markets_per_day: maxMarketsPerDay
      });
      if (metaError) throw metaError;
      onAdd();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add partner');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-bold flex items-center gap-2">
            <User className="w-5 h-5 text-wayja-teal" />
            Add Partner
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Select User</label>
            <input
              type="text"
              placeholder="Search by email..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full border rounded px-3 py-2 mb-2"
            />
            <div className="max-h-32 overflow-y-auto border rounded">
              {filteredUsers.length === 0 ? (
                <div className="p-2 text-gray-500">No users found</div>
              ) : (
                filteredUsers.map(u => (
                  <div
                    key={u.id}
                    className={`p-2 cursor-pointer hover:bg-wayja-teal/10 ${selectedUser?.id === u.id ? 'bg-wayja-teal/20' : ''}`}
                    onClick={() => setSelectedUser(u)}
                  >
                    <span className="font-medium">{u.email}</span>
                    <span className="ml-2 text-xs text-gray-400">({u.role})</span>
                  </div>
                ))
              )}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Partner Name</label>
            <input
              type="text"
              value={partnerName}
              onChange={e => setPartnerName(e.target.value)}
              className="w-full border rounded px-3 py-2"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Commission Rate (%)</label>
            <input
              type="number"
              min={0.1}
              max={50}
              step={0.1}
              value={commissionRate}
              onChange={e => setCommissionRate(Number(e.target.value))}
              className="w-full border rounded px-3 py-2"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Allowed Categories</label>
            <div className="flex flex-wrap gap-2">
              {categories.map(cat => (
                <button
                  type="button"
                  key={cat.id}
                  className={`px-2 py-1 rounded border text-xs ${allowedCategories.includes(cat.name) ? 'bg-wayja-teal text-white border-wayja-teal' : 'bg-white text-gray-700 border-gray-300'}`}
                  onClick={() => handleCategoryToggle(cat.name)}
                >
                  {cat.name} {allowedCategories.includes(cat.name) && <Check className="inline w-3 h-3 ml-1" />}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Max Markets Per Day</label>
            <input
              type="number"
              min={1}
              max={1000}
              value={maxMarketsPerDay}
              onChange={e => setMaxMarketsPerDay(Number(e.target.value))}
              className="w-full border rounded px-3 py-2"
              required
            />
          </div>
          <div className="flex gap-2 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-wayja-teal text-white rounded hover:bg-wayja-teal/90 disabled:opacity-50"
            >
              {loading ? 'Adding...' : 'Add Partner'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
} 